import { useEffect, useState } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { MODULES, CATEGORIES } from "@/lib/modules";
import api from "@/lib/api";
import LegalAlertWatcher from "@/components/LegalAlertWatcher";
import FiscalAlertWatcher from "@/components/FiscalAlertWatcher";
import CheckpointCierreMesWizard from "@/components/CheckpointCierreMesWizard";
import { GlobalSearch } from "@/components/GlobalSearch";
import { OnboardingNudge } from "@/components/OnboardingNudge";
import {
  LogOut, LayoutDashboard, ChevronRight, ChevronDown, Sun, Moon, Menu, X, PanelLeftClose, PanelLeftOpen, BookOpen,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import NotificationCenter from "@/components/NotificationCenter";
import MessagesIndicator from "@/components/MessagesIndicator";
import FilialSelector from "@/components/FilialSelector";
import PendientesFirmaIndicator from "@/components/PendientesFirmaIndicator";
import VencimientosDocBadge from "@/components/VencimientosDocBadge";
import CorreosObraBell from "@/components/CorreosObraBell";
import AICopilotFab from "@/components/AICopilotFab";

const COLLAPSE_KEY = "legatum_sidebar_collapsed";
const CATS_KEY = "legatum_sidebar_cats_open";
// WA0222 · Focus Mode: por defecto TODAS las categorías arrancan cerradas.
// Sólo se auto-expande la que contiene la ruta activa (containsActive en el render).
// El usuario puede abrir otras manualmente y su decisión se persiste.
const isCatOpenRaw = (state, key) => (state[key] !== undefined ? state[key] : false);

export default function Layout({ children, breadcrumb }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const [openMobile, setOpenMobile] = useState(false);
  // WA0120 · categorías del menú desplegables (persistidas)
  const [openCats, setOpenCats] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CATS_KEY)) || {}; } catch { return {}; }
  });
  const toggleCat = (key) => {
    setOpenCats((prev) => {
      const next = { ...prev, [key]: !isCatOpenRaw(prev, key) };
      localStorage.setItem(CATS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // WA0222 · Focus Mode: al cambiar de módulo, se auto-cierran todas las
  // categorías dejando visible únicamente la del módulo activo. El usuario
  // puede abrir otras manualmente hasta el próximo cambio de ruta.
  useEffect(() => {
    setOpenCats(() => {
      const cleared = {};
      try { localStorage.setItem(CATS_KEY, JSON.stringify(cleared)); } catch { /* noop */ }
      return cleared;
    });
  }, [location.pathname]);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  });

  // Iter 166 · Detectar si estamos en escritorio (lg ≥ 1024px). El flag
  // "collapsed" SOLO debe afectar el sidebar de escritorio. En móvil/tablet el
  // drawer SIEMPRE debe mostrar etiquetas completas (de lo contrario el usuario
  // ve solo iconos sueltos y faltan elementos críticos de navegación).
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  // Estado efectivo: en móvil siempre se trata como NO colapsado.
  const collapsedEffective = collapsed && isDesktop;

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const closeMobile = () => setOpenMobile(false);

  // Iter 130.n · Swipe-from-edge para abrir/cerrar el sidebar en móvil
  useEffect(() => {
    let startX = null, startY = null, startedAtEdge = false, startedInside = false;
    const EDGE_PX = 24;          // zona sensible del borde izquierdo
    const OPEN_THRESHOLD = 60;   // px de arrastre para abrir
    const CLOSE_THRESHOLD = 60;  // px de arrastre para cerrar
    const onStart = (e) => {
      if (window.innerWidth >= 1024) return;  // sólo móvil/tablet (<lg)
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY;
      startedAtEdge = !openMobile && startX <= EDGE_PX;
      // Si el menú está abierto y el touch nace dentro del aside, marca para gesto de cierre
      startedInside = openMobile && document.querySelector('[data-testid="sidebar"]')?.contains(e.target);
    };
    const onEnd = (e) => {
      if (startX === null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dy < 80) {  // descarta scroll vertical
        if (startedAtEdge && dx > OPEN_THRESHOLD) setOpenMobile(true);
        if (startedInside && dx < -CLOSE_THRESHOLD) setOpenMobile(false);
      }
      startX = startY = null; startedAtEdge = startedInside = false;
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [openMobile]);

  const visibleModules = MODULES.filter((m) => {
    if (m.hidden) return false;
    // Iter 147 · ocultar módulos deprecados (fusionados, p.ej. Jurídico → LEGAL)
    if (m.deprecated || m.category === "_hidden") return false;
    // Admin ve TODOS los módulos siempre (no se filtra por modules_allowed legacy)
    if (user?.role === "admin") return true;
    const allowed = user?.modules_allowed;
    if (!Array.isArray(allowed) || allowed.length === 0) return true;
    // Compat RBAC legacy: 'avales' o 'garantias' permite ver 'avales_garantias'.
    if (m.key === "avales_garantias") {
      return allowed.includes("avales_garantias") || allowed.includes("avales") || allowed.includes("garantias");
    }
    if (m.key === "subcontratas" || m.key === "adquisiciones") {
      return allowed.includes(m.key) || allowed.includes("adquisiciones") || allowed.includes("compras");
    }
    return allowed.includes(m.key);
  });

  // Iter 96 · Agrupación por categoría manteniendo el orden original.
  const grouped = CATEGORIES.map((cat) => ({
    cat,
    items: visibleModules.filter((m) => m.category === cat.key),
  })).filter((g) => g.items.length > 0);

  // Iter 130.k · contador del badge "CORREO ELECTRÓNICO" en sidebar
  const [correoBadge, setCorreoBadge] = useState(null);
  // Iter 130.ad · badge de alertas fiscales en sidebar
  const [fiscalBadge, setFiscalBadge] = useState(null);
  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = () => {
      api.get("/correo/badge-count")
        .then((r) => alive && setCorreoBadge(r.data))
        .catch(() => {});
      api.get("/fiscal/sl/badge-count")
        .then((r) => alive && setFiscalBadge(r.data))
        .catch(() => {});
    };
    tick();
    timer = setInterval(tick, 60_000);
    // Iter 166g · Refresco inmediato cuando un correo se marca leído
    const onCorreoLeido = () => {
      api.get("/correo/badge-count")
        .then((r) => alive && setCorreoBadge(r.data))
        .catch(() => {});
    };
    window.addEventListener("correo:leido", onCorreoLeido);
    return () => {
      alive = false;
      if (timer) clearInterval(timer);
      window.removeEventListener("correo:leido", onCorreoLeido);
    };
  }, []);

  return (
    <div className="min-h-screen flex bg-[var(--legatum-bg)] text-[var(--legatum-text)] legatum-grain">
      {/* Mobile overlay */}
      {openMobile && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={closeMobile} />
      )}

      {/* Iter 130.l · Watcher de escalados a Gerencia (toast + beep suave) */}
      <LegalAlertWatcher />

      {/* Iter 130.ad · Watcher de alertas fiscales */}
      <FiscalAlertWatcher />

      {/* Iter 130.af · Checkpoint pre-cierre de mes (auto del día 25 en adelante) */}
      <CheckpointCierreMesWizard />

      {/* WA0244 · Botón "¿Cómo empezar?" (TourFAB) eliminado por petición del usuario */}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 shrink-0 border-r border-[var(--legatum-border)] bg-[var(--legatum-surface)]/95 lg:bg-[var(--legatum-surface)]/70 backdrop-blur-md flex flex-col z-40 transition-[width,transform] duration-300 ${
          openMobile ? "translate-x-0 w-[86vw] max-w-[340px]" : "-translate-x-full lg:translate-x-0"
        } ${collapsedEffective ? "lg:w-[68px]" : "lg:w-72"}`}
        data-testid="sidebar"
        data-collapsed={collapsedEffective ? "1" : "0"}
      >
        <div className={`border-b border-[var(--legatum-border)] flex items-center ${collapsedEffective ? "lg:justify-center lg:px-2 px-5 justify-between" : "justify-between px-5"} py-6`}>
          <Link
            to="/"
            data-testid="sidebar-brand"
            onClick={closeMobile}
            className="flex items-center gap-3 min-w-0"
            title={collapsedEffective ? "LEGATUM CAPITAL · SAAS" : undefined}
          >
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, var(--legatum-accent), var(--legatum-accent-2))" }}
            >
              <span className="font-bold tracking-wider" style={{ color: "var(--legatum-accent-fg)" }}>L</span>
            </div>
            {!collapsedEffective && (
              <div className="leading-tight">
                <div className="text-sm font-bold tracking-wider">LEGATUM</div>
                <div className="text-[10px] text-[var(--legatum-text-muted)] tracking-[0.2em]">CAPITAL · SAAS</div>
              </div>
            )}
          </Link>
          <button
            onClick={closeMobile}
            className="lg:hidden h-8 w-8 rounded-md hover:bg-black/[0.05] dark:hover:bg-white/5 flex items-center justify-center"
            aria-label="Cerrar menú"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className={`flex-1 overflow-y-auto ${collapsedEffective ? "lg:px-2" : "px-2"} py-4 space-y-1`}>
          <NavLink
            to="/"
            end
            onClick={closeMobile}
            data-testid="nav-dashboard"
            title="Dashboard — Panel principal con indicadores globales"
            className={({ isActive }) =>
              `flex items-center gap-3 ${collapsedEffective ? "lg:justify-center lg:px-2" : "px-3"} px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                isActive
                  ? "sidebar-item-active text-[var(--legatum-text)]"
                  : "text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] hover:bg-black/[0.04] dark:hover:bg-white/5"
              }`
            }
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            {!collapsedEffective && <span>Dashboard</span>}
          </NavLink>

          {!collapsedEffective && (
            <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-[var(--legatum-text-muted)] font-bold">
              Módulos
            </div>
          )}
          {collapsedEffective && <div className="pt-4 pb-1 lg:mx-2 border-t border-[var(--legatum-border)]" />}

          {grouped.map(({ cat, items }) => {
            const containsActive = items.some((m) => location.pathname === m.route || location.pathname.startsWith(m.route + "/"));
            const catOpen = isCatOpenRaw(openCats, cat.key) || containsActive;
            return (
            <div
              key={cat.key}
              className="rounded-xl mb-1.5 mx-1 p-1.5 border-2 bg-[var(--legatum-surface)] shadow-sm"
              style={{ borderColor: cat.color, boxShadow: cat.pinned ? `0 0 0 1px ${cat.color}55, 0 2px 8px ${cat.color}22` : `0 0 0 1px ${cat.color}22` }}
              data-testid={`nav-cat-${cat.key}`}
            >
              {!collapsedEffective && (
                <button
                  type="button"
                  onClick={() => toggleCat(cat.key)}
                  className={`w-full px-3 py-1.5 flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase font-bold cursor-pointer select-none`}
                  style={{ color: cat.color }}
                  data-testid={`nav-cat-toggle-${cat.key}`}
                >
                  <span className={`${cat.pinned ? "h-2 w-2 animate-pulse" : "h-1.5 w-1.5"} rounded-full shrink-0`} style={{ background: cat.color }} />
                  <span className="flex-1 leading-tight text-left">{cat.label}</span>
                  {!catOpen && <span className="text-[9px] tracking-normal font-extrabold opacity-70 shrink-0">{items.length}</span>}
                  {cat.pinned && (
                    cat.key === "correo" && correoBadge?.total > 0 ? (
                      <span
                        className="text-[10px] tracking-normal px-2 py-0.5 rounded-full font-extrabold tabular-nums shadow-sm shrink-0"
                        style={{ background: cat.color, color: "#fff" }}
                        title={`${correoBadge.emails_pendientes} emails · ${correoBadge.plazos_criticos} plazos críticos · ${correoBadge.kratos_pendientes || 0} notificaciones`}
                      >
                        {correoBadge.total > 99 ? "99+" : correoBadge.total}
                      </span>
                    ) : (
                      <span className="text-[8px] tracking-normal px-2 py-0.5 rounded-full shrink-0" style={{ background: cat.color, color: "#fff" }}>
                        {cat.key === "correo" ? "AL DÍA" : "NUEVO"}
                      </span>
                    )
                  )}
                  {catOpen
                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                    : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                </button>
              )}
              {collapsedEffective && (
                <div className="lg:flex hidden lg:justify-center py-1" title={cat.label}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                </div>
              )}
              {(catOpen || collapsedEffective) && items.map((m) => (
                <NavLink
                  key={m.key}
                  to={m.route}
                  onClick={closeMobile}
                  data-testid={`nav-${m.key}`}
                  title={m.description ? `${m.title} — ${m.description}` : m.title}
                  className={({ isActive }) =>
                    `flex items-center gap-2 ${collapsedEffective ? "lg:justify-center lg:px-2" : "px-3"} px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
                      isActive
                        ? "sidebar-item-active text-[var(--legatum-text)]"
                        : "text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] hover:bg-black/[0.04] dark:hover:bg-white/5"
                    }`
                  }
                >
                  <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: m.color }} />
                  <m.Icon className="h-4 w-4 shrink-0" />
                  {!collapsedEffective && <span className="truncate flex-1 min-w-0">{m.title}</span>}
                  {/* Iter 130.ad · Badge per-módulo para fiscal_sl (alertas vencidas + urgentes) */}
                  {m.key === "fiscal_sl" && fiscalBadge?.total_alertas > 0 && !collapsedEffective && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded-full font-extrabold tabular-nums shrink-0 shadow-sm"
                      style={{
                        background: fiscalBadge.vencidos > 0 ? "#DC2626" : "#E08A1F",
                        color: "#fff",
                      }}
                      title={`${fiscalBadge.vencidos} vencidas · ${fiscalBadge.urgentes_7d} urgentes (≤7d)`}
                      data-testid="sidebar-fiscal-badge"
                    >
                      {fiscalBadge.total_alertas > 99 ? "99+" : fiscalBadge.total_alertas}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>

        <div className={`${collapsedEffective ? "lg:p-2 p-3" : "p-3"} border-t border-[var(--legatum-border)] space-y-2`}>
          {/* Collapse toggle (sólo desktop) */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-collapse-toggle"
            title={collapsedEffective ? "Expandir menú" : "Recoger menú"}
            className={`hidden lg:flex w-full items-center ${collapsedEffective ? "justify-center" : "justify-between"} px-3 py-2 rounded-md text-sm font-semibold hover:bg-black/[0.04] dark:hover:bg-white/5 transition-colors text-[var(--legatum-text-secondary)]`}
          >
            {collapsedEffective ? <PanelLeftOpen className="h-4 w-4" /> : (
              <>
                <span className="flex items-center gap-2"><PanelLeftClose className="h-4 w-4" /> Recoger</span>
                <span className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">⌘\</span>
              </>
            )}
          </button>

          <button
            onClick={toggle}
            data-testid="sidebar-theme-toggle"
            title={collapsedEffective ? (theme === "dark" ? "Modo claro" : "Modo oscuro") : undefined}
            className={`w-full flex items-center ${collapsedEffective ? "justify-center" : "justify-between"} px-3 py-2 rounded-md text-sm font-semibold hover:bg-black/[0.04] dark:hover:bg-white/5 transition-colors`}
          >
            <span className={`flex items-center gap-2 text-[var(--legatum-text-secondary)] ${collapsedEffective ? "" : ""}`}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {!collapsedEffective && (theme === "dark" ? "Modo claro" : "Modo oscuro")}
            </span>
            {!collapsedEffective && <span className="text-[10px] uppercase tracking-widest text-[var(--legatum-text-muted)]">{theme}</span>}
          </button>

          <div className={`${collapsedEffective ? "lg:px-0 lg:justify-center px-2" : "px-2"} py-2 flex items-center gap-3`}>
            <div className="h-8 w-8 rounded-full bg-[var(--legatum-elevated)] flex items-center justify-center text-xs font-bold shrink-0">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            {!collapsedEffective && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate" data-testid="sidebar-user-name">{user?.name || "Usuario"}</div>
                <div className="text-[10px] text-[var(--legatum-text-muted)] uppercase tracking-widest font-semibold">{user?.role}</div>
              </div>
            )}
            {!collapsedEffective && (
              <button
                onClick={handleLogout}
                data-testid="sidebar-logout"
                className="p-2 rounded-md hover:bg-black/[0.04] dark:hover:bg-white/5 text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] transition-colors shrink-0"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
          {collapsedEffective && (
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout-collapsed"
              className="hidden lg:flex w-full items-center justify-center px-3 py-2 rounded-md hover:bg-black/[0.04] dark:hover:bg-white/5 text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative z-10">
        <div className="border-b border-[var(--legatum-border)] px-4 md:px-8 py-3 flex items-center justify-between gap-3 sticky top-0 bg-[var(--legatum-bg)]/85 backdrop-blur-md z-20">
          <div className="flex items-center gap-2 text-xs text-[var(--legatum-text-muted)] font-semibold min-w-0">
            <button
              onClick={() => setOpenMobile(true)}
              data-testid="topbar-menu-button"
              className="lg:hidden h-8 w-8 rounded-md hover:bg-black/[0.05] dark:hover:bg-white/5 flex items-center justify-center -ml-1 mr-1 shrink-0"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5 text-[var(--legatum-text)]" />
            </button>
            <button
              onClick={() => setCollapsed((c) => !c)}
              data-testid="topbar-collapse-toggle"
              className="hidden lg:flex h-8 w-8 rounded-md hover:bg-black/[0.05] dark:hover:bg-white/5 items-center justify-center -ml-1 mr-1 shrink-0"
              aria-label={collapsedEffective ? "Expandir menú lateral" : "Recoger menú lateral"}
              title={collapsedEffective ? "Expandir menú" : "Recoger menú"}
            >
              {collapsedEffective ? (
                <PanelLeftOpen className="h-5 w-5 text-[var(--legatum-text)]" />
              ) : (
                <PanelLeftClose className="h-5 w-5 text-[var(--legatum-text)]" />
              )}
            </button>
            <Link to="/" className="hover:text-[var(--legatum-text)] shrink-0">Inicio</Link>
            {breadcrumb && (
              <>
                <ChevronRight className="h-3 w-3 shrink-0" />
                <span className="text-[var(--legatum-text)] truncate">{breadcrumb}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <GlobalSearch />
            <FilialSelector />
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/manual.pdf`}
              target="_blank"
              rel="noreferrer"
              title="Manual de usuario completo (PDF)"
              data-testid="manual-pdf-link"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-black/[0.05] dark:hover:bg-white/[0.06] text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)] transition-colors"
            >
              <BookOpen className="h-4 w-4" />
            </a>
            <MessagesIndicator />
            <PendientesFirmaIndicator />
            <VencimientosDocBadge />
            <CorreosObraBell />
            <NotificationCenter />
          </div>
        </div>
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1500px]">
          {children}
        </div>
      </main>

      <Toaster theme={theme} position="bottom-right" />
      <OnboardingNudge />
      <AICopilotFab />
    </div>
  );
}
